import { useEffect } from 'react'
import { queries } from '../../../../../api'
import { ChatType } from '../../../../../constants'
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks'
import { selectChat, setOnSelect, setSelectedUser } from '../../../../../redux/reducers/messenger/chatSlice'
import { openCloseSidebar, setMobileMenu } from '../../../../../redux/reducers/messenger/messengerSlice'
import ChatAvatar from '../../../../../shared/image/ChatAvatar'
import PresenceProfile from '../../../../../shared/PresenceProfile'
import { Contact, RecentChatsProps } from '../../../../../types/components/chat'
import { ChatMembersType } from '../../../../../types/store'
import useMediaQuery from '../../../../../utils/useMediaQuery'
import { getShadowRoot } from '../../../../../Webcomponent/utils/common'
import '../../../../../utils/prototype'

const Contacts: React.FC<RecentChatsProps> = ({ onChatSelect, localSearch, onLoading }) => {
  const dispatch = useAppDispatch()
  const { selectedUser, allMembers } = useAppSelector((state) => state.chat)
  const { sidebarToggle } = useAppSelector((state) => state.messenger)
  const isMobile = useMediaQuery('(max-width: 800px)')
  // `localSearch` is already debounced by the caller (the Start Chat modal). A
  // second debounce here delayed and desynced the query from the display
  // condition, so the FIRST search often showed nothing until you typed again —
  // most visibly on browsers that throttle chained timers (e.g. Opera). Use the
  // already-debounced value directly for both the query and the display.
  const activeContactId = selectedUser?.chat_type === ChatType.DM ? selectedUser.id : 0
  const { data: searchData, isLoading } = queries.useSearchContact(localSearch)
  const displayData = searchData && localSearch ? searchData?.contacts : allMembers

  useEffect(() => {
    if (onLoading) {
      onLoading(isLoading)
    }
  }, [isLoading])

  const changeChatClick = (contact: Contact | ChatMembersType) => {
    dispatch(
      selectChat({
        id: contact.chat_id,
        type: contact.chat_type,
        pinned: contact.isPinned,
      }),
    )

    dispatch(setSelectedUser(contact))
    dispatch(setOnSelect(true))

    if (isMobile) {
      // Mobile: reveal the conversation pane. Set the flag AND add the class
      // directly so it works even if state/class have drifted apart.
      dispatch(setMobileMenu(true))
      getShadowRoot()?.querySelector('.sidebar-toggle')?.classList.add('mobile-menu')
    } else if (!sidebarToggle) {
      dispatch(openCloseSidebar(false))
    }

    onChatSelect?.()
  }

  return (
    <>
      {displayData?.length === 0 ? (
        <div className="empty-state text-center p-4">
          <h5>No Contacts found</h5>
          <p className="text-muted">Join or create a Contact to start chatting</p>
        </div>
      ) : (
        <ul className="chat-main">
          {displayData?.map((chat) => (
            <li
              className={`${activeContactId === chat.chat_id ? 'active' : ''}`}
              key={chat?.chat_id}
              onClick={() => changeChatClick(chat)}
            >
              <div className="chat-box">
                <PresenceProfile userId={chat?.chat_id}>
                  <ChatAvatar data={{ avatar: chat?.avatar }} name={{ name: chat.name }} customClass="avatar-sm" />
                </PresenceProfile>
                <div className="details">
                  <h6>{chat.name?.firstTwoWords()}</h6>
                  <p>{chat.bio}</p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </>
  )
}

export default Contacts
